import {TrustPurpose, TrustStatus} from '@sollapay/enums';

import type {TrustAccountAccessSection} from '../utils';
import type {IconName, badgeVariantsConfig} from '@sollapay/ui/components';

type BadgeVariant = keyof (typeof badgeVariantsConfig)['variants']['variant'];

export const TRUST_STATUS_CONFIG: Record<
  TrustStatus,
  {
    variant: BadgeVariant;
    dot: boolean;
    labelKey: string;
    subtitleKey: string | null;
  }
> = {
  [TrustStatus.TRUST_ACTIVE]: {
    variant: 'success',
    dot: true,
    labelKey: 'statuses.trustActive',
    subtitleKey: 'statuses.trustActiveSubtitle'
  },
  [TrustStatus.PENDING_VALIDATION]: {
    variant: 'warning',
    dot: true,
    labelKey: 'statuses.pendingValidation',
    subtitleKey: null
  },
  [TrustStatus.SETUP_IN_PROGRESS]: {
    variant: 'neutral',
    dot: true,
    labelKey: 'statuses.setupInProgress',
    subtitleKey: null
  },
  [TrustStatus.IN_REVIEW]: {
    variant: 'info',
    dot: true,
    labelKey: 'statuses.inReview',
    subtitleKey: null
  },
  [TrustStatus.CLARIFICATION_REQUIRED]: {
    variant: 'error',
    dot: true,
    labelKey: 'statuses.clarificationRequired',
    subtitleKey: null
  },
  [TrustStatus.ACTIVATION_PENDING]: {
    variant: 'error',
    dot: true,
    labelKey: 'statuses.activationPending',
    subtitleKey: null
  }
};

export type TrustPurposeConfig = {
  labelKey: string;
  icon: IconName | null;
  comingSoon?: boolean;
  /** Not yet selectable — excluded from the create form's option list. */
  hidden?: boolean;
};

export const TRUST_PURPOSE_CONFIG: Record<TrustPurpose, TrustPurposeConfig> = {
  [TrustPurpose.SEVEN_PERCENT]: {labelKey: 'trustPurposes.sevenPercent', icon: 'sollapay:building'},
  [TrustPurpose.GENERAL_SALE]: {
    labelKey: 'trustPurposes.generalSale',
    icon: 'sollapay:notebook'
  },
  [TrustPurpose.OPTIONS]: {
    labelKey: 'trustPurposes.options',
    icon: 'sollapay:notebook',
    comingSoon: true
  }
};

/** Duration and easing of the balance hover motion in a list row or card; dropped for reduced-motion users. */
export const BALANCE_HOVER_MOTION_CLASS =
  'duration-350 ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none';

/** Grid for `TrustAccountGridCard`: the column count follows the width of the nearest `@container` (the list area, not the screen). A column needs 21rem, so 2, 3 and 4 columns start at 43, 65 and 87rem. */
export const TRUST_ACCOUNT_GRID_CARD_GRID_CLASS =
  'grid grid-cols-1 gap-4 @[43rem]:grid-cols-2 @[65rem]:grid-cols-3 @[87rem]:grid-cols-4';

/** Goes with `TRUST_ACCOUNT_GRID_CARD_GRID_CLASS`, on the element whose width the card grid's columns follow. */
export const TRUST_ACCOUNT_GRID_CARD_CONTAINER_CLASS = '@container';

/** Dot colours follow the status badge's tone (`TRUST_STATUS_CONFIG`). */
export const TRUST_STATUS_GROUP_CONFIG: Record<
  TrustStatus,
  {titleKey: string; subtitleKey: string | null; dotClassName: string}
> = {
  [TrustStatus.TRUST_ACTIVE]: {
    titleKey: 'statusGroups.active.title',
    subtitleKey: 'statusGroups.active.subtitle',
    dotClassName: 'bg-indication-success'
  },
  [TrustStatus.SETUP_IN_PROGRESS]: {
    titleKey: 'statusGroups.setupInProgress.title',
    subtitleKey: 'statusGroups.setupInProgress.subtitle',
    dotClassName: 'bg-fg-disabled'
  },
  [TrustStatus.PENDING_VALIDATION]: {
    titleKey: 'statusGroups.pendingValidation.title',
    subtitleKey: null,
    dotClassName: 'bg-indication-warning'
  },
  [TrustStatus.IN_REVIEW]: {
    titleKey: 'statusGroups.inReview.title',
    subtitleKey: null,
    dotClassName: 'bg-indication-info'
  },
  [TrustStatus.CLARIFICATION_REQUIRED]: {
    titleKey: 'statusGroups.clarificationRequired.title',
    subtitleKey: null,
    dotClassName: 'bg-indication-error'
  },
  [TrustStatus.ACTIVATION_PENDING]: {
    titleKey: 'statusGroups.activationPending.title',
    subtitleKey: null,
    dotClassName: 'bg-indication-error'
  }
};

export const TRUST_ACCOUNT_ROLE_CONFIG: Record<
  TrustAccountAccessSection,
  {icon: IconName; badgeClassName: string}
> = {
  trustee: {icon: 'shield', badgeClassName: 'bg-fg text-background-surface'},
  operator: {icon: 'tool', badgeClassName: 'bg-brand-primary text-fg_on-brand'},
  viewer: {icon: 'view', badgeClassName: 'bg-background-selected-strong text-fg'}
};
